import { type KeyboardEvent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { IconLineChart, IconWrench } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Chip } from '../../components/ui/Chip'
import { Skeleton } from '../../components/ui/Skeleton'
import { useToast } from '../../components/ui/ToastProvider'
import { VolumePricingPopoverShell } from '../../components/ui/VolumePricingPopoverShell'
import {
  ExternalLinkGlyph,
  FileGlyph,
  SquarePenGlyph,
  Trash2Glyph,
} from '../../components/ui/lucide-glyphs'
import { RowActionsMenuPortal, useRowActionsMenu } from '../../components/ui/rowActionsMenu'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import { formatMoney } from '../cart/cart-money'
import { PartRevBand } from '../parts/components/PartRevBand'
import { isSafePartImageRef } from './AddPartsModal'
import { RfqLineFileChip } from './RfqLineFileChip'
import { addLineToCart, isCartAddSuccess } from './cart-add'
import { RFQ_QUOTE_VARIANTS } from './domain'
import type { QuoteVariant } from './domain'
import {
  isAutoPricedCustomCandidate,
  isCatalogLine,
  isLinePriced,
  needsConfigure,
  previewPriceForVariant,
  previewPriceRange,
  requirementsLabelsForLine,
} from './review-domain'
import type { RfqReviewLine } from './review-domain'
import { useDraftLineDrawings } from './use-draft-line-drawings'

export interface PartsReviewTableProps {
  readonly lines: readonly RfqReviewLine[]
  readonly onQuantityChange: (id: string, quantity: number) => void
  /** Removes the line from this request — the existing behavior, now
   * relocated into the per-row "⋯" menu (UID-114) alongside Move-to-new-RFQ. */
  readonly onRemove: (id: string) => void
  /** UID-114: moves a line OFF this in-progress request and onto a real,
   * separately-submitted RFQ containing just that one part (see
   * `RfqReviewPage.handleMoveToNewRfq` / `review-domain.buildMoveToNewRfqRequest`
   * for why this is a genuine server round trip, not the design stub's
   * fake remove-and-flash). Async at the call site — the table only asks. */
  readonly onMoveToNewRfq: (id: string) => void
  /** Line ids currently mid-move (the create+submit round trip in flight) —
   * disables that row's menu trigger so a second click can't race the first
   * or move an already-moving line again. Defaults to none. */
  readonly movingIds?: readonly string[]
  /** UID-260 (ADR-0085), narrowed by UID-434/WS-D — opens the BULK Configure
   * workflow (`ConfigurePartsModal`) for the bulk toolbar's "Configure N"
   * ONLY, with every currently-selected id that still needs it. The per-row
   * Configure button and the Requirements-cell pencil no longer call this —
   * see `onConfigureLine` below. */
  readonly onConfigure: (ids: readonly string[]) => void
  /** UID-434/WS-D — opens the PER-LINE entry point
   * (`RfqLineConfiguratorModal`, the shared `PartConfiguratorShell`) for
   * exactly one line: the per-row Configure button (`ConfigureButton`) and
   * the Requirements-cell edit pencil (`RequirementsCell`) both call this,
   * never `onConfigure` — the human ruling for this split is that the two
   * per-line entry points must not diverge from each other, only from the
   * bulk toolbar's own entry point. */
  readonly onConfigureLine: (lineId: string) => void
  /** UID-260 gap #1 follow-up — the PREVIEW-ONLY "Choose a quote option"
   * selection (`RfqPreQuotePricingPreview`, owned by `RfqReviewPage`).
   * `undefined` while the customer hasn't picked one yet — each candidate
   * row's Unit-price cell then shows the full `[lo–hi]` range instead of a
   * single price. See `review-domain.ts`'s `previewPriceRange`/
   * `previewPriceForVariant`. */
  readonly selectedPreviewVariantId?: QuoteVariant['id'] | undefined
  /** UID-263 (ADR-0087 D2=FULL/D3) — per-row realignment: called AFTER a
   * successful catalog add-to-cart (2xx or 409, ADR-0087 D3's 409-as-success),
   * so the line can be removed from the RFQ draft, silently, no confirm
   * modal (unlike the bulk path). `RfqReviewPage` owns the removal +
   * discard-draft-and-navigate-when-empty side effect — this table only
   * reports "this line just made it to the cart." */
  readonly onAddedToCart: (id: string) => void
  /** UID-464 (ADR-0151 D4) — true while `RfqBundleProvider` is re-resolving
   * the prices ADR-0103 D5 stripped from the `sessionStorage` snapshot
   * (`useRfqBundle().pricesResolving`).
   *
   * Only affects a line that is neither priced NOR pending configuration:
   * such a line has no price *yet*, and every other reading of "no price" in
   * `UnitPriceCell`'s chain would be a false statement during the round trip
   * (a blank cell for a catalog row; the "Needs quote" pill — the opposite of
   * the truth — for a configured auto-priced one). Defaults to `false`, so
   * every caller and test that doesn't pass it gets the unchanged chain. */
  readonly pricesResolving?: boolean
}

/**
 * RFQ review screen's editable parts table (screens.md §15 "parts table
 * (editable qty)"). Empty state per the cross-screen-states checklist
 * (screens.md's "biggest gap" note) — shown instead of a bare table when
 * nothing has been added yet, so the screen never renders a headerless
 * zero-row table.
 *
 * SCOPE NOTE (UID-260, ADR-0085 — supersedes the UID-242/ADR-0082 note that
 * used to live here): **Configure** (per-row and bulk) and **per-part pricing**
 * are now BUILT. `RfqLineRequest`/`RfqLineView` carry a typed `specSelection`
 * (attributes + server-derived, possibly-still-pending unit price) — the
 * contract + domain-model gap the old note flagged is closed (ADR-0085 D1).
 * A line with no `specSelection` shows the orange "Configure" action below
 * (per-row action cell) instead of a price; the bulk toolbar's "Configure N"
 * does the same for every currently-selected line that still needs it. The
 * Configure workflow itself is a MINIMAL attribute editor
 * (`ConfigurePartsModal`) — see that component's own doc comment for why: no
 * pre-existing Configure UI in this codebase persists to this contract shape,
 * and the design's full per-part-kind configurator can't be built against a
 * typed field list this contract doesn't carry.
 *
 * UID-260 gaps #1/#2/#3/#4/#5 follow-up (frontend-only, this ticket) closed
 * the remaining gaps the note above used to flag:
 *  - **Gap #5 (real bug fix)**: a `kind === 'catalog'` line never carries a
 *    `specSelection` (nothing to Configure — it's already priced from the
 *    catalog), so it must never render the Configure button or count toward
 *    "needs Configure" (`review-domain.ts`'s `needsConfigure`). Before this
 *    fix, ANY RFQ containing a catalog part permanently showed "needs
 *    Configure" and blocked submission.
 *  - **Gap #1 (price range/single preview) + Gap #4 (volume-pricing
 *    popover)**: a non-catalog, `auto_priced` line with a real
 *    `indicativePrice` (`review-domain.ts`'s `isAutoPricedCustomCandidate`)
 *    now shows a PREVIEW `[lo–hi]` range (or a single preview price once a
 *    quote option is picked, gap #2's `RfqPreQuotePricingPreview`) instead
 *    of nothing — see the Unit-price cell below. When that Sourceable also
 *    carries a real `pricingProjection.volumeCurve`, the SAME
 *    `VolumePricingPopoverShell` (ADR-0077) other verticals already use is
 *    wired in via a "Volume" link. `quote_required` custom lines are
 *    UNCHANGED — still the "Needs quote" pill/Configure, no range, no Volume
 *    link (no real base price or curve to scale/show).
 *  - **Gap #3 (catalog add-to-cart)**: a `kind === 'catalog'` row's action
 *    cell now renders a teal "+" add-to-cart icon button (in place of the
 *    Configure button gap #5 removes for it) that POSTs the SAME real
 *    `/cart/lines` endpoint every other add-to-cart entry point uses.
 */
export function PartsReviewTable({
  lines,
  onQuantityChange,
  onRemove,
  onMoveToNewRfq,
  movingIds = [],
  onConfigure,
  onConfigureLine,
  selectedPreviewVariantId,
  onAddedToCart,
  pricesResolving = false,
}: PartsReviewTableProps) {
  // UID-242 Item 2 (REVISED, chrome-only) — selection is pure local UI state:
  // it drives only the bulk toolbar's count/enablement and is never persisted
  // to the bundle store (nothing selection-shaped exists there to persist to,
  // and nothing here needs to survive a remount). UID-610 — `useRangeSelection`
  // additionally carries the Gmail/Finder shift-click anchor.
  const rangeSelection = useRangeSelection()
  const { selectedIds } = rangeSelection
  // UID-528 follow-up — the "Name & drawing" column's chip. Resolved
  // CLIENT-side (a draft line has no server-projected `files` — see
  // `useDraftLineDrawings`'s doc comment) and called BEFORE the empty-state
  // early return below, because hooks may not sit behind a conditional.
  const drawingsByPartId = useDraftLineDrawings(lines.map((l) => l.sourceable.id))
  const { showToast } = useToast()

  if (lines.length === 0) {
    return (
      <p className="rfq-review-parts-empty">
        Add parts to build your request — use &ldquo;Add parts&rdquo; above to pick from your
        library.
      </p>
    )
  }

  const selectedCount = selectedIds.size
  const allSelected = selectedCount > 0 && selectedCount === lines.length
  // Defect follow-up (reported post-UID-260) — the bulk toolbar's "Configure"
  // button used to key off `needsConfigure` per selected row (via a now-removed
  // `selectedNeedingConfigureIds`), so it vanished whenever EVERY selected row
  // was either a catalog line or ALREADY configured. A partial multi-select of
  // two already-configured rows (a totally normal "I want to re-spec these"
  // selection) hid the only entry point that could do it, while select-all —
  // which almost always sweeps in at least one still-unconfigured row — kept
  // showing it. That made the affordance look arbitrary and made bulk
  // RE-configuration unreachable.
  //
  // Fix: the button's visibility and its `onConfigure` target are now BOTH
  // driven by this separate, deliberately-differently-named list — every
  // selected NON-CATALOG line, configured or not. Catalog lines still never
  // qualify (Gap #5 above: no `specSelection`, nothing to configure), so a
  // selection made up only of catalog lines still hides the button. Saving
  // therefore OVERWRITES the specification on any already-configured row in
  // the selection — intentional, matching `PartsBulkConfigureModal`'s own doc
  // comment ("overwrites unconditionally"). `needsConfigure` /
  // `needsConfigureLineIds` themselves are UNTOUCHED by this — they keep
  // driving submit gating and `needsConfigCount` exactly as before.
  const selectedConfigurableIds = lines
    .filter((line) => selectedIds.has(line.id) && !isCatalogLine(line))
    .map((line) => line.id)

  /** UID-610 — shift-click range selection over this table's rendered row
   * order (Gmail/Finder semantics, same as UID-470's Parts Library).
   * `shiftKey` reaches here from the row checkbox's `onClick`, not
   * `onChange` (a `ChangeEvent` carries no modifier-key state). */
  function toggleRow(id: string, shiftKey: boolean) {
    rangeSelection.toggle(
      id,
      lines.map((l) => l.id),
      shiftKey,
    )
  }

  function toggleSelectAll() {
    rangeSelection.toggleAll(
      lines.map((l) => l.id),
      selectedIds.size !== lines.length,
    )
  }

  return (
    <div className="rfq-review-parts-table-wrap">
      {selectedCount > 0 && (
        <div className="rfq-review-bulk-toolbar" role="toolbar" aria-label="Bulk actions">
          <span className="rfq-review-bulk-toolbar-count">{selectedCount} selected</span>
          {/* UID-611 UI fix — "Delete selected" and "Configure" grouped
           * together at the toolbar's trailing edge (delete first), rather
           * than each floating as its own `justify-content: space-between`
           * child (which spread count/Configure/Delete across the full
           * width with Configure stranded in the middle). The count stays
           * the toolbar's only other flex child, so `space-between` now
           * puts it on the left and this whole group on the right. */}
          <div className="rfq-review-bulk-toolbar-actions">
            {/* UID-611 (ADR-0180 D1) — "Delete selected" bulk affordance,
             * using the SAME `Button variant="danger-outline"` pattern
             * `RfqCard.tsx`'s own bulk-delete button uses for the
             * SUBMITTED-RFQ table's "N selected" bar. This draft is
             * client-only local state — no backend call needed, unlike the
             * submitted-RFQ tables' real `remove-batch` endpoint — so this
             * just calls the EXISTING per-line `onRemove` for every selected
             * id, the same seam the row kebab's "Remove from request"
             * already uses.
             *
             * Review-round fix (finding 2, "draft-compose parity") —
             * `RfqReviewPage.handleRemoveLine` (this prop's real
             * implementation) now detects when a batch like this one
             * empties the WHOLE draft and discards the slot + navigates
             * away, mirroring the submitted-RFQ tables' server-side
             * cascade-delete-on-empty rule (ADR-0179 D3) for this purely
             * client-side draft. Calling it once per selected id here is
             * what lets that same handler see the whole batch (it counts
             * distinct ids removed since the last render — see its own doc
             * comment) without this component needing to know anything
             * about that rule itself. */}
            <Button
              type="button"
              variant="danger-outline"
              size="sm"
              className="rfq-review-bulk-toolbar-delete"
              onClick={() => {
                for (const id of selectedIds) {
                  onRemove(id)
                }
                rangeSelection.clear()
              }}
            >
              <Trash2Glyph />
              Delete selected
            </Button>
            {/* UID-260 (ADR-0085) — wired into this EXISTING bulk-select
             * toolbar (UID-242). UID-359 item 7 (`dc.html:4157`) — this is
             * the DIFFERENT, WHITE-outline "Configure" button (no count in
             * the label); the orange, count-free per-row button lives in
             * `UnitPriceCell` below (`dc.html:4226`) — two distinct
             * buttons, kept straight, see this component's SCOPE NOTE.
             *
             * Revised rule (defect follow-up, see `selectedConfigurableIds`
             * above): shown whenever at least one selected row is
             * non-catalog, REGARDLESS of whether it's already configured —
             * not gated on `selectedNeedingConfigureIds`, which hid this
             * button for a partial selection of already-configured rows. A
             * selection made up only of catalog lines still hides it
             * (nothing to configure). `onConfigure` receives every selected
             * non-catalog id, so re-configuring an already-configured
             * selected row deliberately OVERWRITES its specification — see
             * the comment above. */}
            {selectedConfigurableIds.length > 0 && (
              <button
                type="button"
                className="rfq-review-bulk-toolbar-configure"
                onClick={() => onConfigure(selectedConfigurableIds)}
              >
                <IconWrench size={14} />
                Configure
              </button>
            )}
          </div>
          {/* UID-359 item 7 — "Clear selection" is DELETED, not restyled:
           * `dc.html:4153-4158`'s selected-count band has no such control.
           * Per the ticket's own watch-item, selection stays clearable via
           * the row/select-all checkboxes below — nothing is now stuck
           * selected with no way back to zero. */}
        </div>
      )}
      <table className="rfq-review-parts-table">
        <caption className="sr-only">Parts in this request</caption>
        {/* UID-654 (no-scroll fix) — `table-layout: fixed` needs an explicit,
         * order-matched width per column (CSS2.1 §17.5.2: only `<colgroup>`/
         * `<col>` or the FIRST row's cells set column widths; every other
         * row's per-cell width/min-width is ignored for layout). A `<colgroup>`
         * is used rather than widths on the `<thead>` row's `<th>`s so sizing
         * is explicit and independent of which row/element happens to carry a
         * class — see `rfq.css`'s `.rfq-review-col-*` rules for the actual
         * values and the reasoning for which columns are fixed vs. flexible. */}
        <colgroup>
          <col className="rfq-review-col-select" />
          <col className="rfq-review-col-image" />
          <col className="rfq-review-col-name" />
          <col className="rfq-review-col-requirements" />
          <col className="rfq-review-col-qty" />
          <col className="rfq-review-col-price" />
          <col className="rfq-review-col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">
              {/* UID-359 item 8 (`dc.html:8574`'s `cbStyle`) — routed through
               * the shared `.checkbox-input` paint (ui.css) for the base
               * 20x20/checked-brand-primary look. The design's unchecked
               * border/radius (`--border-3`/4px) differ from that shared
               * primitive's own (`--color-border-strong`/5px, pinned for a
               * DIFFERENT prototype element, UID-95 P1) — same reason the RFQ
               * DETAIL table scopes an override to `.rfq-line-select-col
               * .checkbox-input`/`.rfq-parts-selectall-checkbox`
               * (`rfq.css:1007-1018`). This compose table needs the SAME
               * override reached through its own selector — see this
               * component's report for the exact rule the CSS agent must
               * add. The `Checkbox` primitive doesn't fit here: this
               * header control has no visible label (sr-only via
               * `aria-label` only), which `Checkbox.tsx` isn't shaped for. */}
              <input
                type="checkbox"
                aria-label="Select all parts in this request"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="checkbox-input rfq-review-selectall-checkbox"
              />
            </th>
            {/* UID-282 (`.dc.html:4183`) — the 52px part-image column that hosts the
             * custom-part revision caption. Header text is SR-only: the column is a
             * decorative image tile, and the adjacent Name column already labels the
             * row, so a visible "Part" heading would just be redundant chrome the
             * comp does not show. */}
            <th scope="col">
              <span className="sr-only">Part image</span>
            </th>
            <th scope="col">Name &amp; drawing</th>
            {/* UID-333 D1 (ADR-0116) — REQUIREMENTS, not SKU. The design's
             * second column is Requirements (`dc.html:4160`: Name & drawing /
             * Requirements / Qty / Unit price — no SKU column at any width),
             * and `sku` is null for every custom line, so it rendered `—` in
             * exactly the position the design reserves for its most
             * informative cell. SKU is dropped rather than relocated: the
             * part number already appears in the name caption, and nothing
             * else on this screen reads it. */}
            <th scope="col">Requirements</th>
            <th scope="col">Qty</th>
            <th scope="col">Unit price</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id}>
              {/* UID-359 item 8 — same `.checkbox-input` base paint as the
               * select-all checkbox above; see that `<th>`'s comment for why
               * a scoped CSS override (reached via this `<td>`'s class) is
               * still needed for the design's exact unchecked border/radius. */}
              <td className="rfq-review-select-col">
                <input
                  type="checkbox"
                  aria-label={`Select ${line.name}`}
                  checked={selectedIds.has(line.id)}
                  // UID-610 — reads `event.shiftKey` off `onClick` (mouse AND
                  // the Shift+Space keyboard equivalent — a `click` event
                  // carries live modifier state for both, same as
                  // `PartsTable.tsx`'s precedent); `onChange` stays a no-op
                  // since a `ChangeEvent` has no modifier-key fields.
                  onClick={(event) => toggleRow(line.id, event.shiftKey)}
                  onChange={() => {}}
                  className="checkbox-input"
                />
              </td>
              <td className="rfq-review-image-col">
                {/* UID-282 (`.dc.html:4183`): a 52px tile with the revision caption
                 * BELOW it. Consistent with every other RFQ surface (UID-134/UID-196),
                 * the tile is decorative (`aria-hidden`) — the row's own accessible
                 * name lives on the name cell, not here. UID-335 follow-up: the review
                 * line's (widened) `ReviewLineSourceable.imageRef` now carries a real
                 * catalog stock photo or generated CAD thumbnail when the caller had
                 * one at add-time (`toReviewLine`/`add-parts-search.ts`) — falls back
                 * to the empty placeholder box exactly as before when it doesn't,
                 * never a fabricated image. The caption stays a sibling of the tile,
                 * never a child, matching the comp. */}
                <div className="rfq-review-thumb-wrap">
                  <div
                    className="rfq-review-thumb"
                    aria-hidden="true"
                    style={
                      isSafePartImageRef(line.sourceable.imageRef)
                        ? {
                            backgroundImage: `url("${line.sourceable.imageRef.replace(/["\\]/g, '\\$&')}")`,
                          }
                        : undefined
                    }
                  />
                  <PartRevBand rev={line.sourceable.rev} variant="caption" />
                </div>
              </td>
              <td>
                {/* Gate-5 fix — the design's name cell stacks the part number
                 * UNDER the name (`dc.html:4186`, `{{ p.pn }}` below
                 * `{{ p.name }}`). ADR-0116 D1.1 justified dropping the SKU
                 * column on the grounds the part number "already appears in
                 * the name cell's caption" — it did not; this cell was a bare
                 * `{line.name}`. So dropping SKU removed the only place a part
                 * number appeared on this screen. Adding the design's caption
                 * makes that justification true rather than leaving the
                 * information lost. Omitted entirely when absent — no dash. */}
                <span className="rfq-review-name" title={line.name}>
                  {line.name}
                </span>
                {line.sku !== null && line.sku.trim() !== '' && (
                  <span className="rfq-review-name-pn" title={line.sku}>
                    {line.sku}
                  </span>
                )}
                {/* UID-528 follow-up — the DRAWING this column has been named
                 * for since UID-333 but never actually rendered. Same
                 * `RfqLineFileChip` component and same
                 * `.rfq-card-line-files` wrap the submitted-request detail
                 * page uses (`RfqPartsCard`'s name cell), so the two surfaces
                 * paint identically — both classes are unscoped globals in
                 * `rfq.css`, so no CSS change is needed. Drawing ONLY (never
                 * the model/step file), matching the detail page's
                 * `drawingFilesOnly`; here that is structural rather than a
                 * filter, since `useDraftLineDrawings` only ever resolves the
                 * drawing kind. Rendered only when a real drawing resolves —
                 * absent, and this whole block is absent too. Never a
                 * fabricated placeholder. */}
                {(() => {
                  const drawing = drawingsByPartId.get(line.sourceable.id)
                  return drawing === undefined ? null : (
                    <div className="rfq-card-line-files">
                      <RfqLineFileChip
                        partId={line.sourceable.id}
                        file={drawing}
                        onDownloadError={(message) => showToast(message, 'error')}
                      />
                    </div>
                  )
                })()}
              </td>
              <td className="rfq-review-requirements-col">
                <RequirementsCell line={line} onConfigureLine={onConfigureLine} />
              </td>
              <td className="rfq-review-qty-col">
                <input
                  inputMode="numeric"
                  min={1}
                  value={line.quantity}
                  aria-label={`Quantity for ${line.name}`}
                  className="rfq-review-qty-input"
                  onChange={(event) => {
                    const raw = event.target.value
                    // Reject an empty field (don't collapse it to 0), anything
                    // non-numeric, and anything below 1 unit — `min={1}` is
                    // only a native hint with no form submit to enforce it, so
                    // this guard is the real floor. A fraction is floored to
                    // the nearest whole unit rather than rejected outright.
                    if (raw.trim() === '') return
                    const parsed = Number(raw)
                    if (!Number.isFinite(parsed) || parsed < 1) return
                    onQuantityChange(line.id, Math.floor(parsed))
                  }}
                />
              </td>
              {/* UID-359 item 9 (`dc.html:4195`) — the price/pill/Configure
               * group moves INTO this cell (was a dedicated 7th `<td>`,
               * `rfq-review-row-actions`, now deleted — that separate column
               * was the alignment drift item 9 reports). `UnitPriceCell` owns
               * the single branch chain (item 11/D3) that decides which of
               * price / "Needs quote" pill / Configure button renders — see
               * that function's doc comment. The catalog add-to-cart button
               * (gap #3, UID-260/263) has NO design equivalent at all (the
               * design's own `isCart` branch renders only price,
               * `dc.html:4196-4200`) — kept in this SAME right-aligned group,
               * immediately after the price, since it is this table's other
               * catalog-only per-row action and the design reserves the
               * kebab's column for the row menu ONLY. */}
              <td className="rfq-review-price-action-col">
                <span className="rfq-review-price-action-group">
                  <UnitPriceCell
                    line={line}
                    selectedPreviewVariantId={selectedPreviewVariantId}
                    onConfigureLine={onConfigureLine}
                    pricesResolving={pricesResolving}
                  />
                  {isCatalogLine(line) && (
                    <CatalogAddToCartButton line={line} onAdded={onAddedToCart} />
                  )}
                </span>
              </td>
              {/* UID-359 item 9 (`dc.html:4229`) — the kebab's own 28px
               * column, now holding ONLY the row menu (Configure/cart-add
               * moved into the price/action cell above). */}
              <td className="rfq-review-kebab-col">
                {/* UID-654 (table-responsiveness pass) — `id` + the RAW
                 * `onRemove`/`onMoveToNewRfq` (this table's own stable
                 * props), not a `() => onRemove(line.id)` closure built
                 * HERE in the `.map()`: that closure was a new function
                 * identity every render of this table, which defeated
                 * `RowMenu`'s `React.memo` for every row whenever ANY
                 * other row's data changed. `RowMenu` now binds
                 * internally via `useCallback`. */}
                <RowMenu
                  id={line.id}
                  name={line.name}
                  busy={movingIds.includes(line.id)}
                  onRemove={onRemove}
                  onMoveToNewRfq={onMoveToNewRfq}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * UID-333 D1 (ADR-0116) — the Requirements cell, replacing the SKU column.
 *
 * Renders the design's configured spec (`dc.html:4160`'s `Requirements`
 * column): the process as a bold primary label over a `·`-joined sub-line of
 * the chosen attributes, e.g. "Urethane casting" / "Silicone 40A · Matte".
 * All key handling lives in `requirementsLabelsForLine` (ADR-0111 D7's one
 * named accessor per free-form bag) — this component does no key lookups.
 *
 * Two states, deliberately different affordances:
 *
 *  - **Configured** → spec + an inline EDIT pencil, matching the design.
 *  - **Unconfigured** → a "Needs configuration" hint and NO pencil. The
 *    orange Configure button in the row-actions cell is the entry point for
 *    this state, and it stays there.
 *
 * That split is the ADR's D1 point 5. The design's Requirements cell has a
 * pencil and no Configure button, but the pencil is an EDIT affordance for a
 * spec you can already see, whereas the Configure button is the submit GATE:
 * the per-row entry to `ConfigurePartsModal`, the target the bulk toolbar's
 * "Configure N" drives, and the affordance `needsConfigure` gates submission
 * on. Swapping one for the other to close a pixel diff would delete a gate,
 * so both exist — for different states, never both on one row.
 *
 * Never renders a dash. The `—` this column used to show for every custom
 * line IS the defect D1 removes, not a convention to carry forward.
 */
const RequirementsCell = memo(function RequirementsCell({
  line,
  onConfigureLine,
}: {
  readonly line: RfqReviewLine
  readonly onConfigureLine: (lineId: string) => void
}) {
  const { process, detail } = requirementsLabelsForLine(line)
  const handleConfigureLine = useCallback(() => onConfigureLine(line.id), [onConfigureLine, line])

  // Gate-5 fix (Critical) — an earlier pass showed "Needs configuration"
  // whenever there was no spec, which put a FALSE, unactionable label on every
  // catalog row: `needsConfigure` is `!isCatalogLine && !specSelection`
  // (`review-domain.ts`, the UID-260 gap #5 fix), so a catalog line never has
  // a `specSelection`, never gets the Configure button, and never gets the
  // pencil — a dead end on exactly the row type "Priced"/"Add N priced to
  // cart" exists for.
  //
  // The design's own fallback chain (`dc.html:9617`) is
  //   noExtract ? 'Needs configuration'
  //             : (process || cat || (isCustom ? 'Custom part' : 'Catalog part'))
  // i.e. "Needs configuration" is reserved for one specific state, and
  // everything else falls back to a descriptive noun. This build has no
  // extraction step and no `cat` field, but it DOES have a real
  // needs-configuring state — so that is what gates the actionable label, and
  // the descriptive nouns cover the rest. Still never a dash.
  // Awaiting configuration: the Configure button in the row-actions cell is
  // this state's affordance, so no pencil here (two entry points to the same
  // modal on one row is noise).
  if (needsConfigure(line)) {
    return <span className="rfq-review-requirements-empty">Needs configuration</span>
  }

  // UID-346 (ADR-0117 D5) RETIRED this cell's `!hasLabels && editable` branch —
  // the "Custom part" + pencil fallback UID-333's Gate-5 W-2 added.
  //
  // That branch existed because `{ attributes: {} }` was TRUTHY, so an
  // all-blank Configure save marked a line configured, removed the Configure
  // button, and left the labels null — a row with no way back into the modal.
  // D5 closed that at the contract: `SpecSelection` now requires
  // `specification`, and `RfqLineSpecification` requires
  // process/material/finish, so an empty specification is not REPRESENTABLE.
  // The blank-save row now reads `specification == null` -> `needsConfigure`
  // is true -> it short-circuits above with the Configure gate as its
  // affordance. W-2's dead end is fixed more thoroughly than the pencil fixed
  // it, so the pencil branch is unreachable and gone.
  //
  // What remains: past the `needsConfigure` guard a NON-catalog line always
  // has a `specification`, and every required field of one is non-empty — so
  // `process` is never null here and the labels always render. Only a CATALOG
  // line can reach this point label-less (it never carries a specification and
  // never needs one), which is the single fallback below.
  const editable = !isCatalogLine(line)

  if (!editable && process === null && detail === null) {
    return <span className="rfq-review-requirements-fallback">Buy part</span>
  }

  return (
    <span className="rfq-review-requirements">
      {/* The pencil sits ON the process line, not beside the whole two-line
       * stack — that is the design's own nesting (`dc.html:4189`: the process
       * label and the pencil share one `inline-flex;align-items:center` span,
       * with the sub-line a sibling BELOW it). The previous structure made the
       * pencil a sibling of the stack under `align-items:flex-start`, so its
       * 24px box top-aligned to the stack and the glyph landed below the
       * process baseline — the misalignment this fixes. */}
      <span className="rfq-review-requirements-primary">
        {process !== null && <span className="rfq-review-requirements-process">{process}</span>}
        {/* The design's inline pencil (an edit path for a spec that is now
         * visible). Reuses the vendored `SquarePenGlyph` — the same Lucide
         * "square-pen" the RFQ detail rail's "Edit RFQ" control already uses —
         * rather than adding a second pencil to the icon set. Named for what it
         * edits, not "pencil", so the accessible name is useful out of context. */}
        {editable && (
          <button
            type="button"
            className="rfq-review-requirements-edit"
            aria-label={`Edit requirements for ${line.name}`}
            onClick={handleConfigureLine}
          >
            <SquarePenGlyph />
          </button>
        )}
      </span>
      {detail !== null && <span className="rfq-review-requirements-detail">{detail}</span>}
    </span>
  )
})

/**
 * UID-260 (ADR-0085) + gap #1/#4 follow-up, folded together with UID-359
 * items 9/11 (ADR-0118 D3), REORDERED by UID-401 TASK B — the per-row "Unit
 * price" cell (design fact #1, `dc.html:4195`'s right-aligned
 * `min-width:150px` group). ONE branch chain now owns price / "Needs quote"
 * pill / Configure button, which makes AC4 ("a row never shows both the pill
 * and Configure") structural rather than something a test has to police —
 * the pill and the Configure button are returned from mutually exclusive
 * branches of the same if/else-if chain below, so both can never render
 * together, by construction.
 *
 * UID-401 TASK B (overturns this doc comment's OLD point 3, ADR-0118 D3) —
 * ticket bug: "Two parts still needing configuration show pricing anyway."
 * The OLD ordering deliberately preserved co-displaying the gap #1 preview
 * range/price alongside the Configure button for an auto-priced candidate
 * that still needed configuration — reasoned, at the time, as "this app-only
 * preview overlay has no design-enum counterpart, so the Configure gate is
 * preserved alongside it rather than silently dropped." UID-401 overturns
 * that specific co-display: a part that still needs configuration must be
 * either PRICED (from a past config, editable) or NOT PRICED (blocked until
 * configured) — never both a price estimate AND a pending-configuration
 * state on the same row. `needsConfigure` is therefore now the FIRST check
 * in this chain (below), not the fourth — ADR-0118 D3's "unconfigured ⇒
 * Configure, always" invariant is preserved (a `needsConfigure` line still
 * always gets the Configure button — see the regression test pinning this
 * in `PartsReviewTable.test.tsx`), but it can no longer also render a price.
 *
 * This branch order is, by construction, the SAME test `review-domain.ts`'s
 * `isLinePriced` computes (TASK A) — do not let the two drift; see
 * `isLinePriced`'s doc comment and the correspondence test pinning this.
 * UID-464's branch 1a is the ONE exception, and it is not a drift: it is
 * gated on `!isLinePriced(line)` (so it can only ever pre-empt a line the
 * predicate already calls unpriced) and on a flag that is `false` in the
 * steady state, which is why the correspondence pin — which renders without
 * the flag — still holds unmodified.
 *
 * Priority order (highest first):
 *  1. `needsConfigure(line)` — NOT PRICED: no price, no preview range, only
 *     the Configure button (UID-401 TASK B). Always false for a catalog line
 *     (`needsConfigure`'s own definition), so this never disturbs a catalog
 *     row's flat price below.
 *  1a. UID-464 (ADR-0151 D4) — `pricesResolving && !isLinePriced(line)`: the
 *     price is NOT YET KNOWN because ADR-0103 D5 stripped it from the
 *     `sessionStorage` snapshot and the re-resolve is still in flight. Renders
 *     a skeleton. Numbered `1a` rather than `2` because it does not
 *     participate in the `isLinePriced` correspondence below — it is a
 *     TRANSIENT overlay on an otherwise-unpriced line, not a sixth answer to
 *     "is this line priced?", and it resolves within one round trip into
 *     whichever of branches 2-5 the refreshed line actually lands in. Sited
 *     after branch 1 so an unconfigured line still always gets Configure
 *     (ADR-0118 D3), and gated on `!isLinePriced` so a part added THIS
 *     session (never sanitized, real price in hand) keeps showing it.
 *  2. A REAL, server-derived price (`specSelection.unitPriceAmount`) always
 *     wins over any client-side preview — a real backend price beats an
 *     estimate.
 *  3. `kind === 'catalog'` — a flat, region-independent price straight off
 *     the Sourceable's `indicativePrice` (catalog parts are already priced
 *     from the catalog; there is nothing to Configure/quote).
 *  4. Gap #1's PREVIEW range/single-price for a real
 *     `isAutoPricedCustomCandidate` line — plus gap #4's "Volume" link when
 *     that Sourceable also carries a real `pricingProjection.volumeCurve`.
 *     Reaching this branch already proves `needsConfigure(line)` is false
 *     (branch 1 above), so this is a line WITH a past configuration
 *     (`specSelection.specification` present) — PRICED and, per TASK B,
 *     still EDITABLE via the Requirements cell's edit-pencil
 *     (`RequirementsCell`, unaffected by this reorder). No Configure button
 *     co-renders here anymore — there is nothing left to gate.
 *  5. Everything left over — CONFIGURED (branch 1 already guaranteed
 *     `specSelection.specification` exists) but with no price resolved by any
 *     branch above: the "Needs quote" pill, the design's `isNeedsQuote` row
 *     state (`dc.html:4225`). This branch is now the chain's terminal `else`,
 *     no longer narrowed to `pricingOutcome === 'manual_quote_required'` —
 *     see the inline comment at the branch itself for why that narrowing was
 *     wrong (it left the COMMON configured-but-unpriced case rendering an
 *     invented, unstyled "Quote pending" state the design has no slot for).
 *     `manual_quote_required` is now a subset of this branch, not its gate.
 *     Exclusivity against branch 1 still holds BY CONSTRUCTION rather than by
 *     an `else`, mirroring the design's own `ps` being one enum member per
 *     row (`dc.html:7690-7691`'s `libStateCfg['needs-config']`/
 *     `['needs-rfq']`).
 */
const UnitPriceCell = memo(function UnitPriceCell({
  line,
  selectedPreviewVariantId,
  onConfigureLine,
  pricesResolving,
}: {
  readonly line: RfqReviewLine
  readonly selectedPreviewVariantId: QuoteVariant['id'] | undefined
  readonly onConfigureLine: (lineId: string) => void
  readonly pricesResolving: boolean
}) {
  const { specSelection } = line

  // UID-401 TASK B — moved ABOVE every price/preview branch (see this
  // function's doc comment). A line that still needs configuration is never
  // priced, in any of the ways this cell can render a price — only the
  // Configure button. Always false for a catalog line, so a catalog row's
  // flat price (branch below) is unaffected.
  if (needsConfigure(line)) {
    return <ConfigureButton lineId={line.id} onConfigureLine={onConfigureLine} />
  }

  // UID-464 (ADR-0151 D4) — sited SECOND, deliberately: AFTER the
  // `needsConfigure` branch above (so ADR-0118 D3's "unconfigured ⇒
  // Configure, always" invariant is untouched — a re-resolve says nothing
  // about a line's CONFIGURATION state) and BEFORE every price branch below.
  //
  // `!isLinePriced(line)` is what keeps this from suppressing a price the
  // line already has: parts added in THIS session were never sanitized and
  // sit in the same table as rehydrated ones. Reusing that shared predicate
  // rather than re-deriving "has a price" is also what stops this branch
  // drifting from the chain it is spliced into (the ADR-0124/ADR-0087 D1
  // two-surfaces hazard `isLinePriced`'s own doc comment names).
  if (pricesResolving && !isLinePriced(line)) {
    // Gate-5 suggestion (2026-09-04) — `aria-busy` sits on THIS wrapper, not
    // on the `<table>` (too coarse: only the resolving rows' price cells are
    // busy) and not on the `Skeleton` itself (that is `aria-hidden`, so
    // `aria-busy` there is a no-op for AT). It is belt-and-braces, not a
    // compliance fix: `Skeleton`'s own contract names EITHER an `aria-busy`
    // on the region OR visually-hidden loading text as acceptable, and the
    // sr-only span below already satisfies it. The pair helps a user parked
    // on this cell when the DOM swaps under them.
    return (
      <span className="rfq-review-price-cell" aria-busy="true">
        <Skeleton width={64} height={16} />
        <span className="sr-only">Loading price</span>
      </span>
    )
  }

  if (specSelection?.unitPriceAmount) {
    return (
      <span className="rfq-review-price-cell">
        <span className="rfq-review-price-main">
          {formatMoney(specSelection.unitPriceAmount, specSelection.currency ?? 'USD')}
        </span>
        <span className="rfq-review-price-sub">per unit</span>
      </span>
    )
  }

  if (isCatalogLine(line) && line.sourceable.indicativePrice) {
    return (
      <span className="rfq-review-price-cell">
        <span className="rfq-review-price-main">
          {formatMoney(
            line.sourceable.indicativePrice.amount,
            line.sourceable.indicativePrice.currency,
          )}
        </span>
        <span className="rfq-review-price-sub">per unit</span>
      </span>
    )
  }

  if (isAutoPricedCustomCandidate(line)) {
    // UID-401 TASK B — reaching here already proves `needsConfigure(line)`
    // is false (branch above), so this line HAS a past configuration. No
    // Configure button co-renders — there is nothing left to gate; the
    // Requirements cell's edit-pencil is this row's edit entry point.
    return (
      <CandidatePreviewPriceCell line={line} selectedPreviewVariantId={selectedPreviewVariantId} />
    )
  }

  // Gate-5 W4 — keyed on `!specSelection?.specification`, not on the
  // wrapper's truthiness. Reaching this point already proves
  // `needsConfigure(line)` is false, so the only way `specification` can
  // still be absent here is a catalog line with no `indicativePrice` (the
  // one population branch 3 above didn't catch) — nothing to Configure and
  // nothing to show; its own add-to-cart button (rendered by the caller) is
  // its only action.
  if (!specSelection?.specification) return null

  // UID-359 item 11 (ADR-0118 D3), WIDENED — the "Needs quote" pill. Reaching
  // this line already proves `specSelection.specification` exists (the branch
  // above returned otherwise) and that none of the price branches matched, so
  // this is a CONFIGURED line with no resolved price: exactly, and only, the
  // design's `isNeedsQuote` row state (`dc.html:4225`, `lp.ps === 'needs-rfq'`).
  //
  // No longer narrowed to `pricingOutcome === 'manual_quote_required'`. That
  // narrowing left every OTHER configured-but-unpriced line — the COMMON case,
  // since `unitPriceAmount` is server-forced `null` pre-quote for every line
  // (`service.ts`'s `buildSpecSelection`, unconditional) — falling through to
  // a bare, unstyled "Quote pending" `<span>`. That state has no counterpart
  // in the design at all: the compose table's four row states are price /
  // "Needs quote" pill / Configure button / "Quote in progress", and the last
  // of those is a SUBMITTED-RFQ state that lives on the detail and list
  // surfaces (`RfqEtaChip`, `RfqCard`), never on `/rfq/new`. So "Quote pending"
  // was a fifth, invented state occupying the slot the design fills with this
  // pill. `manual_quote_required` still lands here — it is now a subset of
  // this branch rather than the only way in.
  return (
    <Chip tone="violet" icon={<FileGlyph />} className="rfq-review-needs-quote-pill">
      Needs quote
    </Chip>
  )
})

/**
 * UID-359 item 9 — the per-row Configure button (`dc.html:4226`, `it.notCart`)
 * factored out so both the plain "needs Configure" branch and the auto-priced
 * preview branch above (which can ALSO need Configure — see this cell's doc
 * comment) render the exact same button rather than two copies drifting.
 */
const ConfigureButton = memo(function ConfigureButton({
  lineId,
  onConfigureLine,
}: {
  readonly lineId: string
  readonly onConfigureLine: (lineId: string) => void
}) {
  return (
    <button
      type="button"
      className="rfq-review-configure-btn"
      onClick={() => onConfigureLine(lineId)}
    >
      <IconWrench size={14} />
      Configure
    </button>
  )
})

/**
 * UID-260 gap #1 (range/single preview) + gap #4 (volume-pricing popover) —
 * the Unit-price cell's content for a real `isAutoPricedCustomCandidate`
 * line. PREVIEW-ONLY: see `review-domain.ts`'s `previewPriceRange`/
 * `previewPriceForVariant` doc comments — this is a client-side estimate,
 * never a real Quote (ADR-0051).
 */
const CandidatePreviewPriceCell = memo(function CandidatePreviewPriceCell({
  line,
  selectedPreviewVariantId,
}: {
  readonly line: RfqReviewLine
  readonly selectedPreviewVariantId: QuoteVariant['id'] | undefined
}) {
  const selectedVariant = useMemo(
    () =>
      selectedPreviewVariantId
        ? RFQ_QUOTE_VARIANTS.find((variant) => variant.id === selectedPreviewVariantId)
        : undefined,
    [selectedPreviewVariantId],
  )
  const single = useMemo(
    () => (selectedVariant ? previewPriceForVariant(line, selectedVariant) : undefined),
    [selectedVariant, line],
  )
  const range = useMemo(
    () => (single ? undefined : previewPriceRange(line, RFQ_QUOTE_VARIANTS)),
    [single, line],
  )
  const volumeCurve = line.sourceable.pricingProjection?.volumeCurve

  if (!single && !range) return null

  return (
    <span className="rfq-review-price-cell">
      {single ? (
        <>
          <span className="rfq-review-price-main">
            {formatMoney(single.amount, single.currency)}
          </span>
          <span className="rfq-review-price-sub">per unit · {selectedVariant?.leadTimeLabel}</span>
        </>
      ) : (
        range && (
          <>
            <span className="rfq-review-price-main">
              {formatMoney(range.lo, range.currency)}–{formatMoney(range.hi, range.currency)}
            </span>
            <span className="rfq-review-price-sub">per unit · by region</span>
          </>
        )
      )}
      {volumeCurve && (
        <RowVolumePricingLink
          line={line}
          currency={(single ?? range)?.currency ?? 'USD'}
          curve={volumeCurve}
        />
      )}
    </span>
  )
})

/**
 * UID-260 gap #4 follow-up — wires the ALREADY-EXISTING, vertical-agnostic
 * `VolumePricingPopoverShell` (ADR-0077) into this table's Unit-price cell,
 * for the SAME candidate lines gap #1 covers, when that Sourceable also
 * carries a real `pricingProjection.volumeCurve`. Matches the design's
 * "Volume" link affordance (11px / weight 600 / info-blue / line-chart icon)
 * via the `.rfq-review-volume-link` class in `rfq.css`.
 */
const RowVolumePricingLink = memo(function RowVolumePricingLink({
  line,
  currency,
  curve,
}: {
  readonly line: RfqReviewLine
  readonly currency: string
  readonly curve: NonNullable<
    NonNullable<RfqReviewLine['sourceable']['pricingProjection']>['volumeCurve']
  >
}) {
  const { showToast } = useToast()

  return (
    <VolumePricingPopoverShell
      heading="Volume pricing"
      quantity={line.quantity}
      currency={currency}
      curve={curve}
      onAdd={() => postLineToCart(line)}
      onAddSuccess={() => showToast('Added to cart.', 'success')}
      onAddError={() => showToast('Could not add to cart. Please try again.', 'error')}
      // This row is already part of an active RFQ draft — unlike the Parts
      // Library's Qty-cell popover, there is no separate "queue a new RFQ"
      // action that applies here for an above-cap quantity; a no-op is the
      // honest choice rather than fabricating a second RFQ-creation path.
      onRequestRfq={() => {}}
      renderTrigger={({ ref, ariaHasPopup, ariaExpanded, toggle }) => (
        <button
          ref={ref}
          type="button"
          className="rfq-review-volume-link"
          aria-haspopup={ariaHasPopup}
          aria-expanded={ariaExpanded}
          onClick={toggle}
        >
          <IconLineChart size={11} />
          Volume
        </button>
      )}
    />
  )
})

/**
 * UID-260 gap #3 follow-up — the design's teal "+" add-to-cart icon button
 * for a priced `kind === 'catalog'` row, in place of the Configure button
 * gap #5 removes for it. POSTs `/cart/lines` via the shared `addLineToCart`
 * helper (`cart-add.ts`, ADR-0087 D3/D4). UID-616: the summary rail's bulk
 * batch no longer shares this same endpoint (it now POSTs
 * `/cart/lines/add-batch` via `addLinesToCart`) — the two paths share the
 * per-line `AddCartLineRequest` mapping and `CartAddResult` outcome
 * vocabulary, not the wire call itself.
 *
 * UID-263 (ADR-0087 D2=FULL) realignment — REALIGNS this ALREADY-SHIPPED
 * (UID-260/PR#229) button: on a SUCCESSFUL add (2xx or 409, D3's
 * 409-as-success), it now ALSO calls `onAddedToCart(line.id)` so the caller
 * (`RfqReviewPage`) can silently remove the line from the RFQ draft — no
 * confirm modal on this path (unlike bulk), matching `.dc.html`'s
 * `composeAddPart` (L8129-8133): add, then remove, silently, toast only.
 * See `PartsReviewTable.test.tsx`'s CHARACTERIZATION test (legacy-
 * refactoring skill) for the pre-realignment behavior this changes.
 */
const CatalogAddToCartButton = memo(function CatalogAddToCartButton({
  line,
  onAdded,
}: {
  readonly line: RfqReviewLine
  readonly onAdded: (id: string) => void
}) {
  const { showToast } = useToast()
  const [adding, setAdding] = useState(false)

  const handleClick = useCallback(async () => {
    if (adding) return
    setAdding(true)
    try {
      const result = await addLineToCart(line)
      if (isCartAddSuccess(result.outcome)) {
        showToast('Added to cart.', 'success')
        onAdded(line.id)
      } else {
        showToast('Could not add to cart. Please try again.', 'error')
      }
    } finally {
      setAdding(false)
    }
  }, [adding, line, onAdded, showToast])

  return (
    <button
      type="button"
      className="rfq-review-catalog-add-btn"
      aria-label={`Add ${line.name} to cart`}
      disabled={adding}
      onClick={() => void handleClick()}
    >
      <IconPlusGlyph />
    </button>
  )
})

function IconPlusGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

/**
 * UID-260 gap #4's volume-pricing popover expects a THROWING `onAdd`
 * (`VolumePricingPopoverShell`'s contract: `onAdd: () => Promise<void>`,
 * caught internally to call `onAddError`/keep the popover open) — a
 * DIFFERENT contract from the shared `addLineToCart` classification helper
 * (`cart-add.ts`, ADR-0087 D3/D4), which never throws. This is a thin
 * adapter, scoped to the volume popover's own add flow only (a DIFFERENT
 * surface from the per-row `CatalogAddToCartButton`'s realigned
 * remove-from-RFQ behavior above — out of this ticket's realignment scope,
 * unchanged) — translates a classified failure back into a throw so the
 * popover's existing error handling keeps working unmodified.
 */
async function postLineToCart(line: RfqReviewLine): Promise<void> {
  const result = await addLineToCart(line)
  if (!isCartAddSuccess(result.outcome)) {
    throw new Error(result.message ?? "We couldn't add this part to your cart. Try again.")
  }
}

/**
 * A part row's "⋯" menu (UID-114): Move to a new RFQ + Remove from request.
 * While `busy` (a move is in flight for this line), the trigger itself is
 * disabled so a second click can't race the first.
 *
 * UID-570 — migrated off the LOCAL, hand-rolled `position: absolute` panel
 * (UID-359 item 10 had deliberately deferred this — see git history for
 * that tradeoff) onto the shared `useRowActionsMenu`/`RowActionsMenuPortal`
 * primitive (rowActionsMenu.tsx), the same one `PartRowActions`/`RfqCard`/
 * `RfqRowMenu` use: the absolute panel's box used to contribute to the
 * `.rfq-review-parts-table-scroll` wrapper's scrollable content area (that
 * wrapper's `overflow-x: auto` forced `overflow-y: auto` too), so opening
 * this menu on the LAST row grew the table's scrollable height instead of
 * floating over it — reported live. That wrapper has since been removed
 * (the table is no longer a scroll container at all), but the portal stays:
 * it is still the correct way to float this panel over row content rather
 * than push on any ancestor's layout, scrollable or not. The portaled panel
 * is `position: fixed` on `<body>`, immune to any ancestor's overflow.
 *
 * Mirrors `PartRowActions`' exact ARIA-menu migration (UID-421): portaling
 * to `<body>` breaks native Tab order into the panel, so opening it moves
 * focus onto the first `[role="menuitem"]` itself, and `handleMenuKeyDown`
 * roves Up/Down/Home/End between items locally (the shared hook itself has
 * no focus management).
 */
const RowMenu = memo(function RowMenu({
  id,
  name,
  busy,
  onRemove,
  onMoveToNewRfq,
}: {
  readonly id: string
  readonly name: string
  readonly busy: boolean
  readonly onRemove: (id: string) => void
  readonly onMoveToNewRfq: (id: string) => void
}) {
  const handleRemove = useCallback(() => onRemove(id), [onRemove, id])
  const handleMoveToNewRfq = useCallback(() => onMoveToNewRfq(id), [onMoveToNewRfq, id])

  const { open, coords, containerRef, triggerRef, panelRef, menuId, toggleOpen, runAndClose } =
    useRowActionsMenu()

  useEffect(() => {
    if (!open) return
    panelRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()
  }, [open, panelRef])

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    )
    if (items.length === 0) return
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement)

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      items[(currentIndex + 1) % items.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      items[(currentIndex - 1 + items.length) % items.length]?.focus()
    } else if (event.key === 'Home') {
      event.preventDefault()
      items[0]?.focus()
    } else if (event.key === 'End') {
      event.preventDefault()
      items[items.length - 1]?.focus()
    }
  }

  return (
    <div ref={containerRef} className="rfq-review-row-menu">
      <button
        type="button"
        ref={triggerRef}
        className="rfq-review-row-menu-trigger"
        aria-label={busy ? `Moving ${name} to a new RFQ` : `More actions for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={busy}
        onClick={(event) => {
          event.stopPropagation()
          toggleOpen()
        }}
      >
        <KebabIcon />
      </button>
      <RowActionsMenuPortal
        open={open}
        coords={coords}
        menuId={menuId}
        panelRef={panelRef}
        role="menu"
        onKeyDown={handleMenuKeyDown}
      >
        <button
          type="button"
          role="menuitem"
          className="rfq-review-row-menu-item"
          onClick={runAndClose(handleMoveToNewRfq)}
        >
          <ExternalLinkGlyph />
          {/* UID-359 item 10 (`dc.html:4235`) — the design string includes
           * the article "a"; the pre-existing build was missing it. The
           * MODAL title this opens ("Move to a new RFQ?",
           * `MoveToNewRfqConfirmModal`) already said "a" — this fix
           * removes an inconsistency between the two, not creates one. */}
          Move to a new RFQ
        </button>
        <button
          type="button"
          role="menuitem"
          className="rfq-review-row-menu-item rfq-review-row-menu-item-danger"
          onClick={runAndClose(handleRemove)}
        >
          <Trash2Glyph />
          Remove from request
        </button>
      </RowActionsMenuPortal>
    </div>
  )
})

function KebabIcon() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  )
}
